import { GOOGLE_TILE_API_BASE } from './session';

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ViewportQuery {
  zoom: number;
  north: number;
  south: number;
  east: number;
  west: number;
}

export type ViewportCopyrightResult =
  | { status: 'ok'; copyright: string }
  | { status: 'session-invalid' }
  | { status: 'error' };

export const VIEWPORT_THROTTLE_MS = 500;

const clampLat = (value: number) => Math.max(-90, Math.min(90, value));
const clampLng = (value: number) => Math.max(-180, Math.min(180, value));

export function buildViewportUrl(apiKey: string, session: string, query: ViewportQuery): string {
  const params = new URLSearchParams({
    session,
    key: apiKey,
    zoom: String(Math.max(0, Math.round(query.zoom))),
    north: String(clampLat(query.north)),
    south: String(clampLat(query.south)),
    east: String(clampLng(query.east)),
    west: String(clampLng(query.west)),
  });
  return `${GOOGLE_TILE_API_BASE}/tile/v1/viewport?${params.toString()}`;
}

export async function fetchViewportCopyright(
  apiKey: string,
  session: string,
  query: ViewportQuery,
  fetchImpl: FetchLike = (input, init) => fetch(input, init)
): Promise<ViewportCopyrightResult> {
  try {
    const response = await fetchImpl(buildViewportUrl(apiKey, session, query));
    if (response.status >= 400 && response.status < 500) {
      return { status: 'session-invalid' };
    }
    if (!response.ok) {
      return { status: 'error' };
    }
    const body = (await response.json()) as { copyright?: unknown };
    return { status: 'ok', copyright: typeof body.copyright === 'string' ? body.copyright : '' };
  } catch {
    return { status: 'error' };
  }
}

export function escapeAttribution(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface AttributionTarget {
  addAttribution(text: string): unknown;
  removeAttribution(text: string): unknown;
}

export function replaceAttribution(target: AttributionTarget, previous: string | null, next: string | null): string | null {
  if (previous === next) return previous;
  if (previous) target.removeAttribution(previous);
  if (next) target.addAttribution(next);
  return next;
}

export interface ThrottledRunner {
  request(): void;
  cancel(): void;
}

export function createThrottledRunner(
  run: () => void,
  intervalMs: number = VIEWPORT_THROTTLE_MS,
  now: () => number = Date.now
): ThrottledRunner {
  let lastRun = Number.NEGATIVE_INFINITY;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const fire = () => {
    timer = null;
    lastRun = now();
    run();
  };

  return {
    request() {
      if (timer) return;
      const wait = lastRun + intervalMs - now();
      if (wait <= 0) {
        fire();
      } else {
        timer = setTimeout(fire, wait);
      }
    },
    cancel() {
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}
